/**
 * How many times an appointment slot is filled, and what that becomes on the wire.
 *
 * A slot has bounds, and the form used to ignore them and draw exactly one
 * picker per role. That made three of the four sports' headline cases
 * unenterable — a syndicate is routinely forty owners, a bout is scored by three
 * judges, a Test has two on-field umpires — even though the API accepted all of
 * them. The arithmetic lives here rather than in the component so it can be
 * tested without a DOM.
 */

import type { RoleSlot } from "@/lib/api/types";

/** The fewest pickers a slot ever shows. Always at least one: a slot with no
 *  floor still needs somewhere to put the first person. */
export function floorOf(slot: Pick<RoleSlot, "min">): number {
  return Math.max(slot.min ?? 0, 1);
}

/**
 * The pickers to draw: what has been chosen, padded up to the floor.
 *
 * Padded rather than hidden, because three blank judge rows say "this needs
 * three" better than a hint under a single dropdown.
 */
export function rowsFor(slot: Pick<RoleSlot, "min">, chosen: string[]): string[] {
  const floor = floorOf(slot);
  if (chosen.length >= floor) return chosen;
  return [...chosen, ...Array<string>(floor - chosen.length).fill("")];
}

/** Whether another appointment fits. `max` of null or undefined means no ceiling. */
export function hasRoom(slot: Pick<RoleSlot, "max">, rows: string[]): boolean {
  return slot.max === null || slot.max === undefined || rows.length < slot.max;
}

/**
 * Whether this slot is one person or a list.
 *
 * `max === 1` keeps the plain single picker it always had — most slots, and they
 * should not grow an add button they can never use.
 */
export function isSingle(slot: Pick<RoleSlot, "max">): boolean {
  return slot.max === 1;
}

/** The appointments a fresh form starts with: the floor, unfilled. */
export function blankAppointments(slots: RoleSlot[]): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const slot of slots) out[slot.role] = Array<string>(floorOf(slot)).fill("");
  return out;
}

/** What the API takes: one row per filled appointment, blanks dropped. */
export function appointmentsToInput(
  chosen: Record<string, string[]>,
): { role: string; participant: string }[] {
  return Object.entries(chosen).flatMap(([role, people]) =>
    people.filter(Boolean).map((participant) => ({ role, participant })),
  );
}
